import { useAuthAxios } from '@/backend/utils';
import authHeader from '@/backend/auth/auth-header';

const http = useAuthAxios();

const API_PREFIX = '/opened-monitor-bot/api/';
const ERP_DISTRIBUTION_PREFIX = '/distribution/';

function serializeParams(params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item === undefined || item === null) return;
        search.append(key, String(item));
      });
      return;
    }
    search.append(key, String(value));
  });
  return search.toString();
}

export function fetchOrders(params, { signal } = {}) {
  return http.get(API_PREFIX + 'distr', {
    params,
    signal,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    paramsSerializer: { serialize: serializeParams },
  }).then((res) => res.data);
}

export function exportOrders(params) {
  return http.get(ERP_DISTRIBUTION_PREFIX + 'export', {
    params,
    responseType: 'blob',
    headers: {
      'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ...authHeader(),
    },
    paramsSerializer: { serialize: serializeParams },
  });
}

export function fetchSources() {
  return http.get(API_PREFIX + 'sources', {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  }).then((res) => res.data);
}

export function fetchCityOptions(params, { signal } = {}) {
  return http.get(API_PREFIX + 'distr/cities', {
    params,
    signal,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    paramsSerializer: { serialize: serializeParams },
  }).then((res) => res.data);
}

export function fetchClientOptions(params, { signal } = {}) {
  return http.get(API_PREFIX + 'distr/clients', {
    params,
    signal,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    paramsSerializer: { serialize: serializeParams },
  }).then((res) => res.data);
}

export function fetchClientRegistryStatus() {
  return http.get(API_PREFIX + 'distr/client-registry-status', {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  }).then((res) => res.data);
}

export function fetchApprovals(params) {
  return http.get(ERP_DISTRIBUTION_PREFIX + 'approvals', {
    params,
    headers: authHeader(),
    paramsSerializer: { serialize: serializeParams },
  }).then((res) => res.data);
}

function callDistributionOperation(uid, suffix, method = 'post', data) {
  const request = method === 'delete'
    ? http.delete(`${ERP_DISTRIBUTION_PREFIX}${uid}${suffix}`, { headers: authHeader() })
    : http[method](`${ERP_DISTRIBUTION_PREFIX}${uid}${suffix}`, data, { headers: authHeader() });
  return request.then((res) => res.data);
}

export function joinQueue(uid) {
  return callDistributionOperation(uid, '/pending');
}

export function leaveQueue(uid) {
  return callDistributionOperation(uid, '/pending', 'delete');
}

export function takeWork(uid) {
  return callDistributionOperation(uid, '/work');
}

export function releaseWork(uid) {
  return callDistributionOperation(uid, '/work', 'delete');
}

export function prolongWork(uid, extraMin = 120) {
  return callDistributionOperation(uid, '/work/prolong', 'post', { extra_min: extraMin });
}

export function bookInternal(uid) {
  return callDistributionOperation(uid, '/book');
}

export function releaseBooking(uid) {
  return callDistributionOperation(uid, '/book', 'delete');
}

export function bookInternalPriority(uid) {
  return callDistributionOperation(uid, '/book/priority');
}

export function closeOrder(uid, payload) {
  return callDistributionOperation(uid, '/close', 'post', payload);
}

export function closeBelowBase(uid, payload) {
  return callDistributionOperation(uid, '/close/below-base', 'post', payload);
}

export function reopenClosedOrder(uid, payload) {
  return callDistributionOperation(uid, '/close/cancel/reopen', 'post', payload);
}

export function finallyCancelClosedOrder(uid, payload) {
  return callDistributionOperation(uid, '/close/cancel/final', 'post', payload);
}

export function reopenCanceledOrder(uid) {
  return callDistributionOperation(uid, '/reopen-canceled');
}

export function acceptContextUpdate(uid) {
  return callDistributionOperation(uid, '/context-update/accept');
}

export function reopenWithContextDisagreement(uid, payload) {
  return callDistributionOperation(uid, '/context-update/reopen', 'post', payload);
}

export function setManualStatus(uid, payload) {
  return callDistributionOperation(uid, '/manual-status', 'post', payload);
}

export function resetManualStatus(uid) {
  return callDistributionOperation(uid, '/manual-status', 'delete');
}

export function setManualCost(uid, payload) {
  return callDistributionOperation(uid, '/manual-cost', 'post', payload);
}

export function resetManualCost(uid) {
  return callDistributionOperation(uid, '/manual-cost', 'delete');
}

export function confirmApproval(uid) {
  return callDistributionOperation(uid, '/approve/confirm');
}

export function denyApproval(uid) {
  return callDistributionOperation(uid, '/approve/deny');
}

export function createApproval(uid, payload) {
  return callDistributionOperation(uid, '/approve', 'post', payload);
}

export function deleteApproval(uid) {
  return callDistributionOperation(uid, '/approve', 'delete');
}

export function fetchSubsidyTable(uid) {
  return http.get(`${ERP_DISTRIBUTION_PREFIX}${uid}/subsidy-table`, { headers: authHeader() })
    .then((res) => res.data);
}
